import { useState } from "react";
import { Box, Typography, Paper, Tabs, Tab } from "@mui/material";
import { useCurrentUser } from "@/context/CurrentUserContext";
import { PromptList } from "./components/PromptList";
import { PromptEditor } from "./components/PromptEditor";
import { VersionHistory } from "./components/VersionHistory";
import { LLMModels } from "./components/LLMModels";
import type { Prompt } from "../../services/promptsApi";
import "../../assets/css/Text-General.css";
import "../../assets/css/tabs.css";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  if (value !== index) {
    return null;
  }

  return (
    <Box
      sx={{
        px: 3,
        pb: 3,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "auto",
        minHeight: 0,
      }}
      {...other}
    >
      {children}
    </Box>
  );
}

export const PromptManagement = () => {
  const { role, canEdit } = useCurrentUser();
  const isAdmin = role === "ADMIN";
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [selectedPromptKey, setSelectedPromptKey] = useState<string | null>(
    null,
  );
  const [tabValue, setTabValue] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePromptSelect = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    setSelectedPromptKey(prompt.prompt_key);
    setTabValue(1);
  };

  const handleSaveSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
    setTabValue(0);
    setSelectedPrompt(null);
    setSelectedPromptKey(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        padding: "12px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box sx={{ marginBottom: "10px", flexShrink: 0 }}>
        <Typography
          className="text-general-page-header-prefix"
          sx={{ paddingBottom: "0 !important", mb: "0 !important" }}
        >
          Prompt Management
        </Typography>
        <Typography
          className="text-general-page-header"
          sx={{ mb: "0 !important" }}
        >
          Manage LLM prompts used throughout the application
        </Typography>
      </Box>

      <Paper
        elevation={2}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="prompt management tabs"
            TabIndicatorProps={{ className: "tf-tabs__indicator" }}
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              px: 2,
            }}
          >
            <Tab className="tf-tab" label="Prompt List" />
            <Tab
              className="tf-tab"
              label={selectedPrompt ? "Edit Prompt" : "Create Prompt"}
            />
            <Tab className="tf-tab" label="Version History" />
            <Tab className="tf-tab" label="LLM Models" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <PromptList
            onPromptSelect={handlePromptSelect}
            refreshTrigger={refreshTrigger}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {isAdmin && canEdit ? (
            <PromptEditor
              prompt={selectedPrompt}
              onSaveSuccess={handleSaveSuccess}
              onCancel={() => {
                setTabValue(0);
                setSelectedPrompt(null);
                setSelectedPromptKey(null);
              }}
            />
          ) : (
            <Typography color="text.secondary">
              {!canEdit
                ? "You do not have permission to edit these prompts."
                : "Only admin users have permissions to edit these prompts"}
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <VersionHistory promptKey={selectedPromptKey} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {canEdit ? (
            <LLMModels />
          ) : (
            <Typography color="text.secondary">
              You do not have permission to edit LLM models.
            </Typography>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default PromptManagement;
